import { getOutlets as getOutletsService, getOutletById as getOutletByIdService } from '../services/outlets';

/**
 * Fetches outlets from the API
 * @param {string} baseUrl - Base URL for the API
 * @param {Object} [options] - Optional query parameters
 * @param {number} [options.limit] - The limit for the number of records to be returned
 * @param {number} [options.page] - The page number used when paginating the data
 * @param {string} [options.search] - Outlet code to search for. If provided, returns only the matching outlet
 * @returns {Promise<Object>} - Promise resolving to the response with outlets data. If search is provided, returns filtered response with matching outlet
 */
export const getOutlets = async (baseUrl, options = {}) => {
  try {
    const params = {
      baseUrl,
      limit: options.limit ?? 25,
      page: options.page ?? 1,
      search: options.search ?? "",
    };

    const response = await getOutletsService(params);
    
    // If search code is provided, find the matching outlet
    if (options.search && response?.items) {
      const foundOutlet = response.items.find((outlet) => outlet.code === options.search);
      if (foundOutlet) {
        // Return the full response structure but with only the matching outlet
        return {
          ...response,
          items: [foundOutlet],
        };
      }
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Extracts the outletId from the outlets response
 * @param {Object} outletsResponse - The response from getOutlets (already filtered if search was provided)
 * @returns {string|null} - The first outlet ID, or null if no outlets found
 */
export const extractOutletId = (outletsResponse) => {
  if (!outletsResponse || !outletsResponse.items || !Array.isArray(outletsResponse.items) || outletsResponse.items.length === 0) {
    return null;
  }
  
  return outletsResponse.items[0].id || null;
};

/**
 * Fetches a single outlet by ID
 * @param {string} outletId - The outlet ID
 * @param {string} baseUrl - Base URL for the API
 * @returns {Promise<Object>} - Promise resolving to the outlet data
 */
export const getOutletById = async (outletId, baseUrl) => {
  try {
    return await getOutletByIdService(outletId, baseUrl);
  } catch (error) {
    throw error;
  }
};
